import React, { useState, useEffect } from 'react';
import SimulationSettingsPanel from '../shared/SimulationSettingsPanel';
import { simulateLegs } from '../../utils/hedgeSimulation';

/**
 * HedgingResults — renders the output of /stock-analyzer/hedging/.
 *
 * Sections (style mirrors ElasticityTable):
 *   1. Position summary + recommended (loss-cap) put hedge
 *   2. Scenario P&L table (Unhedged vs Hedged vs Straddle), red→green colored
 *   3. Two-Way Straddle card (trim + puts + calls sized to profit either way)
 *   4. Live simulation panel over the combined legs (client-side Black-Scholes)
 *   5. Collapsible put-candidate and call-candidate comparison tables
 */
function HedgingResults({ data }) {
  const [showCandidates, setShowCandidates] = useState(false);
  const [showCalls, setShowCalls] = useState(false);

  const simLegs = data?.sim_legs || [];
  const [includedLegs, setIncludedLegs] = useState(() => simLegs.map(() => true));
  const [sim, setSim] = useState({ days: null, gain: 10 });

  // Reset leg toggles whenever a fresh result arrives.
  useEffect(() => {
    setIncludedLegs((data?.sim_legs || []).map(() => true));
    setSim({ days: null, gain: 10 });
  }, [data]);

  if (!data) return null;

  if (data.error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
        <p className="text-red-300 text-sm">{data.error}</p>
      </div>
    );
  }

  const {
    position, recommendation, scenario_table = [], candidates = [],
    call_candidates = [], straddle, parameters,
  } = data;

  const fmtCurrency = (v) => {
    if (v == null || isNaN(v)) return '--';
    const n = Number(v);
    const sign = n < 0 ? '-' : '';
    return `${sign}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };
  const fmtPremium = (v) => (v == null || isNaN(v) ? '--' : `$${Number(v).toFixed(2)}`);
  const fmtPct = (v) => (v == null || isNaN(v) ? '--' : `${Number(v).toFixed(1)}%`);

  // Diverging red→green coloring scaled by the largest magnitude in the table.
  const scale =
    scenario_table.reduce((max, r) => {
      const vals = [r.unhedged, r.hedged, r.straddle].filter((x) => x != null);
      const local = vals.reduce((m, x) => Math.max(m, Math.abs(x)), 0);
      return Math.max(max, local);
    }, 0) || 1;

  const pnlColor = (v, customScale) => {
    if (v == null || isNaN(v)) return {};
    const ratio = Math.min(Math.abs(v) / (customScale || scale), 1);
    const opacity = 0.12 + ratio * 0.45;
    const rgb = v >= 0 ? '34, 197, 94' : '239, 68, 68';
    return { backgroundColor: `rgba(${rgb}, ${opacity.toFixed(2)})` };
  };

  const targetMove = parameters?.target_move_pct;

  // --- Live simulation over the combined legs --------------------------------
  const minDte = simLegs.reduce((m, l) => Math.min(m, Number(l.dte) || Infinity), Infinity);
  const maxSimDays = Number.isFinite(minDte) ? Math.max(1, minDte - 1) : 60;
  const defaultSimDays = Math.min(parameters?.horizon_days || 30, maxSimDays);
  const simDays = sim.days == null ? defaultSimDays : sim.days;
  const S0 = position ? Number(position.underlying_price) : 0;
  const full = simLegs.length ? simulateLegs(simLegs, S0, simDays, sim.gain) : { perLeg: [] };
  const simTotal = full.perLeg.reduce((s, p, i) => s + (includedLegs[i] ? p.pnl : 0), 0);

  return (
    <div className="space-y-6">
      {/* Position summary */}
      {position && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryStat label="Underlying" value={`${position.ticker} @ $${Number(position.underlying_price).toFixed(2)}`} />
          <SummaryStat label="Position Value" value={fmtCurrency(position.current_value)} />
          <SummaryStat
            label={`Unhedged @ ${targetMove}%`}
            value={fmtCurrency(recommendation?.unhedged_at_target ?? cellAt(scenario_table, targetMove, 'unhedged'))}
            negative
          />
          <SummaryStat label="Worst case (unhedged)" value={fmtCurrency(position.worst_case_unhedged)} negative />
        </div>
      )}

      {/* Recommendation card */}
      {recommendation ? (
        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🛡️</span>
            <h3 className="text-lg font-semibold text-white">Recommended Hedge (cap the downside)</h3>
            {recommendation.cap_met ? (
              <span className="text-xs px-2 py-0.5 rounded bg-green-700/50 text-green-200">Caps your loss</span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded bg-yellow-700/50 text-yellow-200">Best available</span>
            )}
          </div>
          <p className="text-gray-200 mb-4">{recommendation.summary}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <KeyVal label="Buy" value={`${recommendation.contracts_needed} × $${Number(recommendation.strike).toFixed(0)} puts`} />
            <KeyVal label="Expiration" value={recommendation.expiration} />
            <KeyVal label="Put Premium" value={fmtPremium(recommendation.premium)} />
            <KeyVal label="Hedge Cost" value={`${fmtCurrency(recommendation.hedge_cost)} (${fmtPct(recommendation.hedge_cost_pct)})`} />
          </div>
        </div>
      ) : (
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-md p-4">
          <p className="text-yellow-200 text-sm">
            No protective-put candidates were found for this position / horizon. Try a longer
            horizon or check that the ticker has a liquid options chain.
          </p>
        </div>
      )}

      {/* Scenario P&L table */}
      {scenario_table.length > 0 && (
        <div>
          <h3 className="text-md font-semibold text-white mb-3">
            Projected P&amp;L over {parameters?.horizon_days} days
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-max">
              <thead>
                <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
                  <th className="text-left py-2 px-3 text-white" style={{ backgroundColor: '#1a1a1a' }}>Underlying Move</th>
                  <th className="text-right py-2 px-3 text-white" style={{ backgroundColor: '#1a1a1a' }}>Unhedged</th>
                  <th className="text-right py-2 px-3 text-white" style={{ backgroundColor: '#1a1a1a' }}>Hedged (recommended)</th>
                  <th className="text-right py-2 px-3 text-white" style={{ backgroundColor: '#1a1a1a' }}>Straddle</th>
                </tr>
              </thead>
              <tbody>
                {scenario_table.map((row) => (
                  <tr
                    key={row.move_pct}
                    className={`border-b border-gray-800 ${row.is_target ? 'ring-1 ring-blue-500/40' : ''}`}
                  >
                    <td className="py-2 px-3 font-medium text-white">
                      {row.move_pct > 0 ? '+' : ''}
                      {row.move_pct}%{row.is_target && <span className="ml-2 text-xs text-blue-400">target</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-white" style={pnlColor(row.unhedged)}>{fmtCurrency(row.unhedged)}</td>
                    <td className="py-2 px-3 text-right text-white" style={pnlColor(row.hedged)}>{fmtCurrency(row.hedged)}</td>
                    <td className="py-2 px-3 text-right text-white" style={pnlColor(row.straddle)}>
                      {row.straddle == null ? '--' : fmtCurrency(row.straddle)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            P&amp;L = change in position value over the horizon under each scenario. Green = gain /
            protected, red = loss.
          </p>
        </div>
      )}

      {/* Two-Way Straddle */}
      {straddle && (
        <div className="bg-gray-700/30 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">⚖️</span>
            <h3 className="text-md font-semibold text-white">Two-Way Straddle (Trim + Both Directions)</h3>
            {straddle.both_directions_profitable ? (
              <span className="text-xs px-2 py-0.5 rounded bg-green-700/50 text-green-200">Profits both ways</span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded bg-yellow-700/50 text-yellow-200">Best effort within budget</span>
            )}
          </div>
          <p className="text-gray-300 text-sm mb-4">{straddle.summary}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <KeyVal label="Trim" value={`${(straddle.trim_fraction * 100).toFixed(0)}% → ${fmtCurrency(straddle.cash_freed)}`} />
            <KeyVal
              label="Protective Puts"
              value={straddle.n_put ? `${straddle.n_put} × $${Number(straddle.put.strike).toFixed(0)} (${fmtCurrency(straddle.put.cost)})` : 'none'}
            />
            <KeyVal
              label="Higher-risk Calls"
              value={straddle.n_call ? `${straddle.n_call} × $${Number(straddle.call.strike).toFixed(0)} (${fmtCurrency(straddle.call.cost)})` : 'none'}
            />
            <KeyVal label="Protected Cash" value={fmtCurrency(straddle.cash_kept_protected)} />
          </div>
          <div className="mt-3 flex flex-wrap gap-6 text-sm">
            <div>
              At −{straddle.move_pct}%:{' '}
              <span className={straddle.down_pnl >= 0 ? 'text-green-300' : 'text-red-300'}>{fmtCurrency(straddle.down_pnl)}</span>
            </div>
            <div>
              At +{straddle.move_pct}%:{' '}
              <span className={straddle.up_pnl >= 0 ? 'text-green-300' : 'text-red-300'}>{fmtCurrency(straddle.up_pnl)}</span>
            </div>
            <div className="text-gray-400">
              Range across scenarios: <span className="text-red-300">{fmtCurrency(straddle.worst_case)}</span> …{' '}
              <span className="text-green-300">{fmtCurrency(straddle.best_case)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Live simulation over the combination */}
      {simLegs.length > 0 && position && (
        <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-4">
          <h3 className="text-md font-semibold text-white mb-1">Simulate the Combination</h3>
          <p className="text-xs text-gray-400 mb-3">
            Drag the sliders to re-price every leg with Black-Scholes. Toggle legs to see each one's
            contribution.
          </p>
          <SimulationSettingsPanel
            maxDays={maxSimDays}
            defaultDays={defaultSimDays}
            defaultGain={10}
            isActive
            defaultExpanded
            label="Combination"
            contractCount={simLegs.length}
            onRefresh={(days, gain) => setSim({ days, gain })}
            onReset={() => setSim({ days: defaultSimDays, gain: 10 })}
          />

          <div className="flex items-center justify-between bg-gray-900/50 rounded-md px-4 py-3 mb-3">
            <div className="text-sm text-gray-300">
              Combined P&amp;L at {sim.gain > 0 ? '+' : ''}{sim.gain}% in {simDays} days
            </div>
            <div className={`text-xl font-bold ${simTotal >= 0 ? 'text-green-300' : 'text-red-300'}`}>
              {fmtCurrency(simTotal)}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-max">
              <thead>
                <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
                  <th className="text-left py-2 px-2 text-white" style={{ backgroundColor: '#1a1a1a' }}>Include</th>
                  <th className="text-left py-2 px-2 text-white" style={{ backgroundColor: '#1a1a1a' }}>Leg</th>
                  <th className="text-right py-2 px-2 text-white" style={{ backgroundColor: '#1a1a1a' }}>Sim Premium</th>
                  <th className="text-right py-2 px-2 text-white" style={{ backgroundColor: '#1a1a1a' }}>Leg P&amp;L</th>
                </tr>
              </thead>
              <tbody>
                {simLegs.map((leg, i) => {
                  const r = full.perLeg[i] || {};
                  const on = includedLegs[i];
                  return (
                    <tr key={i} className={`border-b border-gray-800 ${on ? '' : 'opacity-40'}`}>
                      <td className="py-2 px-2">
                        <input
                          type="checkbox"
                          checked={!!on}
                          onChange={() => setIncludedLegs((prev) => prev.map((v, j) => (j === i ? !v : v)))}
                        />
                      </td>
                      <td className="py-2 px-2 text-left text-gray-200">
                        {leg.label}
                        <span className={`ml-2 text-[10px] px-1 rounded ${leg.group === 'owned' ? 'bg-gray-600 text-gray-200' : 'bg-blue-700/50 text-blue-200'}`}>
                          {leg.group}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtPremium(r.simPremium)}</td>
                      <td className="py-2 px-2 text-right text-white" style={on ? pnlColor(r.pnl) : {}}>
                        {fmtCurrency(r.pnl)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Put candidates */}
      {candidates.length > 0 && (
        <div>
          <button onClick={() => setShowCandidates((s) => !s)} className="text-sm text-blue-400 hover:text-blue-300">
            {showCandidates ? '▼ Hide' : '▶ Show'} all {candidates.length} put candidates
          </button>
          {showCandidates && (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs min-w-max">
                <thead>
                  <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
                    {['Strike', 'Expiration', 'DTE', 'Premium', 'IV', '# to Cap', 'Hedge Cost', '% of Pos', 'Protection @ Target', 'Breakeven'].map((h) => (
                      <th key={h} className="text-right py-2 px-2 text-white" style={{ backgroundColor: '#1a1a1a' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c, i) => (
                    <tr key={i} className="border-b border-gray-800 hover:bg-gray-700/30">
                      <td className="py-2 px-2 text-right text-white font-medium">${Number(c.strike).toFixed(0)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{c.expiration}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{c.dte}</td>
                      <td className="py-2 px-2 text-right text-white">{fmtPremium(c.premium)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtPct(c.iv)}</td>
                      <td className="py-2 px-2 text-right text-white">{c.contracts_needed == null ? '--' : c.contracts_needed}</td>
                      <td className="py-2 px-2 text-right text-white">{fmtCurrency(c.hedge_cost)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtPct(c.hedge_cost_pct)}</td>
                      <td className="py-2 px-2 text-right text-white" style={pnlColor(c.protection_at_target)}>{fmtCurrency(c.protection_at_target)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtPremium(c.breakeven)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Call candidates */}
      {call_candidates.length > 0 && (
        <div>
          <button onClick={() => setShowCalls((s) => !s)} className="text-sm text-blue-400 hover:text-blue-300">
            {showCalls ? '▼ Hide' : '▶ Show'} all {call_candidates.length} call candidates (upside legs)
          </button>
          {showCalls && (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs min-w-max">
                <thead>
                  <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
                    {['Strike', '% OTM', 'Expiration', 'DTE', 'Premium', 'IV', 'Leverage', 'Gain @ +Move', 'Breakeven'].map((h) => (
                      <th key={h} className="text-right py-2 px-2 text-white" style={{ backgroundColor: '#1a1a1a' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {call_candidates.map((c, i) => (
                    <tr key={i} className="border-b border-gray-800 hover:bg-gray-700/30">
                      <td className="py-2 px-2 text-right text-white font-medium">${Number(c.strike).toFixed(0)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtPct(c.strike_pct)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{c.expiration}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{c.dte}</td>
                      <td className="py-2 px-2 text-right text-white">{fmtPremium(c.premium)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtPct(c.iv)}</td>
                      <td className="py-2 px-2 text-right text-white">{c.leverage == null ? '--' : `${Number(c.leverage).toFixed(2)}x`}</td>
                      <td className="py-2 px-2 text-right text-white" style={pnlColor(c.gain_at_up)}>{fmtCurrency(c.gain_at_up)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtPremium(c.breakeven)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, negative }) {
  return (
    <div className="bg-gray-900/40 border border-gray-700 rounded-lg p-3">
      <div className="text-xs text-gray-400">{label}</div>
      <div className={`text-base font-semibold ${negative ? 'text-red-300' : 'text-white'}`}>{value}</div>
    </div>
  );
}

function KeyVal({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-white font-medium">{value}</div>
    </div>
  );
}

// Read a column value at a given move from the scenario table.
function cellAt(table, move, key) {
  const row = table.find((r) => r.move_pct === move);
  return row ? row[key] : null;
}

export default HedgingResults;
